import { formatDate } from 'util/formatDate';
import { formatTime } from 'util/formatTime';
import { pxToEm } from 'util/pxToEm';
import { Image, TruncateText } from 'components/lib';
import { Box, Typography, useTheme, Button } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import React, { ReactElement, useState } from 'react';
import useSWR from 'swr';
import { FavoriteEventButton } from 'components/app/FavoriteEventButton';
import { Event, useEventsApi } from 'services/api';
import { useRouter } from 'next/router';
import { Routes, adminEventReviewUrl } from 'services/routes';
import { EventStatus } from 'enum/event-status';
import { RecurringIcon, CalendarClockIcon } from 'components/icons';
import { ViewAllDatesModal } from '../ViewAllDatesModal';

export interface EventBriefAdminProps {
  recurrenceLabel?: string;
  id: Event['id'];
  status: EventStatus;
  title: string;
  link: string;
  date: Date;
  favorite?: boolean;
  hideFavorite?: boolean;
  startTime?: string;
  endTime?: string;
  bannerSrc: string;
  isRecurring?: boolean;
  school: {
    iconUrl: string;
    logoUrl: string;
    link: string;
    name: string;
  };
  organizations: {
    logoUrl: string;
    link: string;
    name: string;
  }[];
  style?: object | [];
  disableLinks?: boolean;
  maxWidth?: object;
  minWidth?: object;
}

export const EventBriefAdmin = ({
  recurrenceLabel,
  id,
  status,
  title,
  date,
  startTime,
  endTime,
  bannerSrc,
  isRecurring = false,
  style = {},
  hideFavorite = false,
  maxWidth = {
    sx: '100%',
    sm: '100%',
    md: 280,
    lg: 280,
  },
  minWidth = {
    sx: '100%',
    sm: '100%',
    md: 280,
    lg: 280,
  },
}: EventBriefAdminProps): ReactElement => {
  const theme = useTheme();
  const router = useRouter();
  const eventsApi = useEventsApi();

  const [showViewAllDatesModal, setShowViewAllDatesModal] = useState(false);
  const [occurrenceFilter, setOccurrenceFilter] = useState<'upcoming' | 'past'>('upcoming');

  const { data: occurrences, isValidating: occurrencesLoading, mutate: mutateOccurrences } = useSWR(
    isRecurring && showViewAllDatesModal
      ? `/events/${id}/occurrences?filter=${occurrenceFilter}`
      : null,
    async () => {
      const result = await eventsApi.getEventOccurrences(id, occurrenceFilter);
      if (result.success) return result.value;
      return [];
    },
  );

  const { data: occurrenceCounts, mutate: mutateOccurrenceCounts } = useSWR(
    isRecurring ? `/events/${id}/occurrence-counts` : null,
    async () => {
      const result = await eventsApi.getEventOccurrenceCounts(id);
      if (result.success) return result.value;
      return { upcoming: 0, past: 0 };
    },
  );

  const handleViewAttendees = (occurrenceId: string) => {
    router.push(Routes.admin.eventAttendees(occurrenceId));
  };

  const handleCheckInAttendees = (occurrenceId: string) => {
    router.push(Routes.admin.eventCheckIn(occurrenceId));
  };

  const handleDeleteOccurrence = async (occurrenceId: string) => {
    const result = await eventsApi.deleteChildOccurrence(occurrenceId);
    if (result.success) {
      mutateOccurrences();
      mutateOccurrenceCounts();
    }
  };

  const handleViewAllDatesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowViewAllDatesModal(true);
  };

  return (
    <>
      <Box
        onClick={() => router.push(adminEventReviewUrl(id, status))}
        width="100%"
        maxWidth={{
          ...maxWidth,
        }}
        minWidth={{
          ...minWidth,
        }}
        sx={[
          {
            backgroundColor: '#fff',
            textAlign: 'left',
            border: '1px solid #E8EAEF',
            lineHeight: 0,
            boxShadow: 0,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: '1px 1px 15px rgba(0,0,0,.2)',
            },
          },
          ...(Array.isArray(style) ? style : [style]),
        ]}
      >
        {!hideFavorite && (
          <Box
            position="relative"
            top={4}
            left={4}
            fontSize={35}
            height="0px"
            width="0px"
          >
            <FavoriteEventButton eventId={id} />
          </Box>
        )}
        <Image
          src={bannerSrc}
          alt={title}
          width={280}
          height={280}
          style={{
            objectFit: 'cover',
          }}
        />
      </Box>

      <Box sx={{ paddingTop: 16 }}>
        <Box mb={8}>
          <Typography
            variant="h3"
            style={{
              color: theme.palette.text.primary,
              lineHeight: pxToEm(24),
              display: 'flex',
              alignItems: 'center',
              fontWeight: 500,
              fontSize: pxToEm(16),
            }}
          >
            <TruncateText lines={2}>{title}</TruncateText>
          </Typography>
          {isRecurring && (
            <Box
              width="100%"
              maxWidth={{
                ...maxWidth,
              }}
              minWidth={{
                ...minWidth,
              }}
              mt={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  minWidth: 300,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  p: '8px 12px',
                  backgroundColor: '#F0F9FF',
                  borderRadius: '6px',
                }}
              >
                <RecurringIcon sx={{ fontSize: 16, color: '#00627A', mt: '8px', flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: pxToEm(12),
                    fontWeight: 500,
                    color: '#00627A',
                    lineHeight: 1.5,
                  }}
                >
                  {recurrenceLabel || 'Loading recurrence details...'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {!isRecurring && (
          <>
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: '#777777',
                whiteSpace: 'nowrap',
              }}
            >
              {formatDate(date, {
                includeDay: true,
                includeDate: true,
                includeYear: true,
              })}
            </Typography>
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: '#777777',
                whiteSpace: 'nowrap',
              }}
            >
              {startTime && formatTime(startTime)}
              {startTime && endTime && ' - '}
              {endTime && formatTime(endTime)}
            </Typography>
          </>
        )}
      </Box>

      <Box
        width="100%"
        maxWidth={{
          ...maxWidth,
        }}
        minWidth={{
          ...minWidth,
        }}
        mt={8}
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {isRecurring ? (
          <Button
            variant="contained"
            startIcon={<CalendarClockIcon />}
            onClick={handleViewAllDatesClick}
            sx={{
              minWidth: 300,
              backgroundColor: theme.palette.vibrant.primary,
              color: theme.palette.common.black,
              '&:hover': {
                backgroundColor: theme.palette.vibrant.primary,
              },
              textTransform: 'none',
              px: 44,
              py: 10,
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            View All Dates
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<QrCodeIcon />}
            onClick={() => router.push(Routes.admin.eventCheckIn(id))}
            sx={{
              minWidth: 300,
              backgroundColor: theme.palette.vibrant.primary,
              color: theme.palette.common.black,
              '&:hover': {
                backgroundColor: theme.palette.vibrant.primary,
              },
              textTransform: 'none',
              px: 44,
              py: '10px',
              borderRadius: '6px',
              fontSize: pxToEm(14),
              fontWeight: 400,
            }}
          >
            Check in attendees
          </Button>
        )}
      </Box>

      {isRecurring && (
        <ViewAllDatesModal
          open={showViewAllDatesModal}
          onClose={() => setShowViewAllDatesModal(false)}
          eventTitle={title}
          occurrences={occurrences || []}
          isLoading={occurrencesLoading}
          filter={occurrenceFilter}
          onFilterChange={setOccurrenceFilter}
          counts={occurrenceCounts}
          onViewAttendees={handleViewAttendees}
          onCheckInAttendees={handleCheckInAttendees}
          onDeleteOccurrence={handleDeleteOccurrence}
          mode="admin"
        />
      )}
    </>
  );
};
